// Import react elements
import React, { useEffect, useRef } from "react";

// Import components
import { LoadingSpinner } from "global/components/loading_ui/LoadingSpinnerAnimation";

// Import hooks
import { useProductModule } from "global/hooks/useProductModule";

// Import styles
import "./ProductSamplePopup.css";

// This popup lets the shared product module handle sample selection for products with variants or colourways.
const ProductSamplePopup = ({
    isOpen,
    onClose,
    storeAlias = "",
    sku = "",
    showPrice = false,
}) => {

    // Keep one mount element for the shared product card.
    const productCardMountRef = useRef(null);

    // Load the shared product module only while this popup is open.
    const {
        renderProductCard,
        isReady,
        isLoading,
        status,
    } = useProductModule({
        enabled: isOpen,
    });

    // Render the product card into the popup once the module is ready.
    useEffect(() => {

        // Stop when the popup is closed.
        if (!isOpen) {
            return undefined;
        }

        // Stop when the store alias or sku are missing.
        if (!storeAlias || !sku) {
            return undefined;
        }

        // Wait until the script is ready and the mount element exists.
        if (!isReady || !productCardMountRef.current) {
            return undefined;
        }

        // Keep the shared module cleanup handle so we can destroy the embedded UI when this effect reruns.
        let renderState = null;

        try {

            // Let the shared product module build the full sample-selection UI.
            renderState = renderProductCard(productCardMountRef.current, {
                storeAlias,
                sku,
                previewControls: "thumbnail",
                showPrice,
                displayPrice: showPrice,
            });

        } catch (error) {

            // Surface render failures in the console so popup issues can still be debugged without crashing React.
            console.error("Could not render sample product card.", error);

        }

        // Clean up the shared product module when the popup closes or rerenders.
        return () => {
            renderState?.destroy?.();
        };

    }, [isOpen, isReady, renderProductCard, showPrice, sku, storeAlias]);

    // When shared selectors change, switch the embedded product card back to the first preview entry.
    useEffect(() => {

        // Stop when the popup is closed or the card mount does not exist yet.
        if (!isOpen || !productCardMountRef.current) {
            return undefined;
        }

        // Reuse the live mount element so DOM listeners stay attached to the shared product module root only.
        const mountElement = productCardMountRef.current;

        // Track queued animation frame IDs so we can cancel any pending preview resets during cleanup.
        let firstFrameId = 0;
        let secondFrameId = 0;

        // Reset the shared preview controls back to the first image/button after a colourway or variant change.
        const resetToFirstPreview = () => {

            // Find the first shared preview control button rendered by the external product module.
            const firstPreviewButton = mountElement.querySelector(".strich_product_preview_controls button:first-child");

            // Reuse the module's own click behavior instead of duplicating preview-switching logic locally.
            firstPreviewButton?.click?.();
        };

        // Delay the preview reset until after the external module has finished reacting to the selector change.
        const queuePreviewReset = () => {

            // Let the shared module finish its own rerender first, then jump back to the first preview.
            firstFrameId = window.requestAnimationFrame(() => {

                // Wait one extra frame so nested module updates have also settled before we trigger the preview button.
                secondFrameId = window.requestAnimationFrame(resetToFirstPreview);
            });

        };

        // Watch clicks inside the mounted module so colourway swatch changes can reset the preview selection.
        const handleColourwayClick = (event) => {

            // Only react to clicks on the shared colourway swatches.
            if (!event.target?.closest?.("button[data-colourway-code]")) {
                return;
            }

            // A new colourway was picked, so jump the shared preview controls back to the first preview item.
            queuePreviewReset();

        };

        // Watch change events inside the mounted module so child product dropdowns can also reset the preview.
        const handleVariantChange = (event) => {

            // Only react to the shared child-product option dropdowns.
            if (!event.target?.closest?.(".strich_product_option select")) {
                return;
            }

            // A new child product option was picked, so jump the shared preview controls back to the first preview item.
            queuePreviewReset();

        };

        // Register a delegated click listener on the mount root so dynamically rendered swatches still trigger resets.
        mountElement.addEventListener("click", handleColourwayClick);

        // Register a delegated change listener on the mount root so dynamically rendered dropdowns also trigger resets.
        mountElement.addEventListener("change", handleVariantChange);

        return () => {

            // Remove the delegated click listener when the popup closes or the effect reruns.
            mountElement.removeEventListener("click", handleColourwayClick);

            // Remove the delegated change listener for the same reason.
            mountElement.removeEventListener("change", handleVariantChange);

            // Cancel any pending first-frame reset callback so it cannot run after cleanup.
            if (firstFrameId) {
                window.cancelAnimationFrame(firstFrameId);
            }

            // Cancel any pending second-frame reset callback as well.
            if (secondFrameId) {
                window.cancelAnimationFrame(secondFrameId);
            }
        };

    }, [isOpen]);

    // If the popup is closed, render nothing.
    if (!isOpen) {
        return null;
    }

    // Treat a missing store alias or sku as an unsaved/incomplete product state that cannot open sample ordering.
    const missingConfig = !storeAlias || !sku;

    return (

        <div className="popup-overlay">

            {/* Keep the sample popup styling aligned with the shared popup layout, with an extra modifier when price should stay hidden. */}
            <div className={`popup-container sampleProductPopup ${showPrice ? "" : "sampleProductPopup--hidePrice"}`.trim()}>

                {/* Render the popup header with the sample-ordering title and close action. */}
                <div className="popup__header">

                    <div className="popup__headerLeft">

                        <h2 className="popup__title">
                            Order Sample
                        </h2>

                    </div>

                    <div className="popup__headerRight">

                        {/* Let the parent close the popup when the user clicks the standard close button. */}
                        <button type="button" className="popup__close" onClick={() => onClose?.()} title="Close">
                            x
                        </button>

                    </div>

                </div>

                <div className="popup-content sampleProductPopup__content">

                    {/* Show a save/config warning instead of the module UI when we do not have enough product info yet. */}
                    {missingConfig &&

                        <div className="warning-message">
                            Save this product and make sure a store is selected before ordering a sample.
                        </div>

                    }

                    {/* Show the shared loading spinner while the external product module script is still loading. */}
                    {!missingConfig && isLoading &&

                        <LoadingSpinner message="Loading product sample options..." />

                    }

                    {/* Surface a simple warning when the external product module script fails to load. */}
                    {!missingConfig && status === "error" &&

                        <div className="warning-message">
                            Could not load the product sample options.
                        </div>

                    }

                    <div
                        ref={productCardMountRef}
                        className="sampleProductPopup__cardMount"

                        // Hide the mount root until the product module is ready so users do not see an empty container flash.
                        style={{ display: missingConfig || !isReady ? "none" : "block" }}
                    ></div>

                </div>

            </div>

        </div>

    );

};

export default ProductSamplePopup;
